import React, { createContext, useContext } from "react";
import {
  Empty as ShadcnEmpty,
  EmptyHeader as ShadcnEmptyHeader,
  EmptyTitle as ShadcnEmptyTitle,
  EmptyDescription as ShadcnEmptyDescription,
  EmptyContent as ShadcnEmptyContent,
  EmptyMedia as ShadcnEmptyMedia,
} from "src/shadcn/components/empty";
import { cn } from "src/shadcn/lib/utils";

const RESPONSIVE_MAX_WIDTH = "max-w-full sm:max-w-md lg:max-w-lg";

const InnerClassNameContext = createContext<string | undefined>(undefined);

// Tailwind-merge resolves conflicts only within the same responsive variant, so
// a consumer-supplied `max-w-2xl` cannot override `sm:max-w-md` / `lg:max-w-lg`
// on its own. When the consumer provides any `max-w-*` token, drop our
// responsive defaults entirely and let their value take over at every breakpoint.
const resolveSlotClassName = (
  inner: string | undefined,
  local: string | undefined
) => {
  const consumer = cn(inner, local);
  const hasMaxWidth = consumer.includes("max-w-");

  return hasMaxWidth ? consumer : cn(RESPONSIVE_MAX_WIDTH, consumer);
};

type EmptyProps = React.ComponentProps<typeof ShadcnEmpty> & {
  innerClassName?: string;
};

const Empty = ({ innerClassName, ...props }: EmptyProps) => (
  <InnerClassNameContext.Provider value={innerClassName}>
    <ShadcnEmpty {...props} />
  </InnerClassNameContext.Provider>
);

const EmptyHeader = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnEmptyHeader>) => {
  const inner = useContext(InnerClassNameContext);

  return (
    <ShadcnEmptyHeader
      className={resolveSlotClassName(inner, className)}
      {...props}
    />
  );
};

const EmptyTitle = (props: React.ComponentProps<typeof ShadcnEmptyTitle>) => (
  <ShadcnEmptyTitle {...props} />
);

const EmptyDescription = (
  props: React.ComponentProps<typeof ShadcnEmptyDescription>
) => <ShadcnEmptyDescription {...props} />;

const EmptyContent = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnEmptyContent>) => {
  const inner = useContext(InnerClassNameContext);

  return (
    <ShadcnEmptyContent
      className={resolveSlotClassName(inner, className)}
      {...props}
    />
  );
};

const EmptyMedia = (props: React.ComponentProps<typeof ShadcnEmptyMedia>) => (
  <ShadcnEmptyMedia {...props} />
);

export {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  EmptyMedia,
};
